import React, { useEffect } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { Header } from './components/Header';
import { CallButton } from './components/CallButton';
import { Home } from './pages/Home';
import { Services } from './pages/Services';
import { Work } from './pages/Work';
import { Reviews } from './pages/Reviews';
import { About } from './pages/About';
import { Areas } from './pages/Areas';
import { Faq } from './pages/Faq';
import { Delta } from './pages/Delta';
import { Contact } from './pages/Contact';
import { PaintersDelta } from './pages/PaintersDelta';
import { Footer } from './components/Footer';
import { SectionPage } from './pages/SectionPage';
import useReveal from './hooks/useReveal';
import useSeo from './hooks/useSeo';
import { SITE, locationPages, sectionPages } from './routes';

export { SITE };

/** Which homepage section component renders on each standalone section page. */
const sectionComponents: Record<string, React.ComponentType> = {
  services: Services,
  work: Work,
  reviews: Reviews,
  about: About,
  areas: Areas,
  faq: Faq,
  delta: Delta,
  contact: Contact,
};

/** The full one-page homepage. A hash (/#about) scrolls to that section. */
function HomePage() {
  const location = useLocation();

  useSeo({
    title: 'Ivy League Painting | Interior, Strata & Commercial Painters in Delta, BC',
    description:
      "Delta's #1 painting company. Interior painting, kitchen cabinet painting, strata, commercial and warehouse painting across the Lower Mainland. BBB A+ accredited. Get a free quote today.",
    canonical: SITE + '/',
  });

  useEffect(() => {
    const target = location.hash.replace('#', '');
    if (!target) return;
    const section = document.getElementById(target);
    if (section) section.scrollIntoView({ behavior: 'smooth' });
    // Re-run only when the router location changes (a click), not on scroll-spy URL updates.
  }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <main>
      <Home />
      <Services />
      <Work />
      <Reviews />
      <About />
      <Areas />
      <Faq />
      <Delta />
      <Contact />
    </main>
  );
}

const App: React.FC = () => {
  const location = useLocation();
  useReveal([location.pathname]);

  // A new page starts at the top, unless the link targets a section (/#about).
  useEffect(() => {
    if (!location.hash) window.scrollTo({ top: 0 });
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <Header links={sectionPages} pages={locationPages} />
      <Routes>
        {sectionPages.map((page) => {
          const Section = sectionComponents[page.id];
          return (
            <Route
              key={page.to}
              path={page.to}
              element={
                <SectionPage page={page}>
                  <Section />
                </SectionPage>
              }
            />
          );
        })}
        <Route path="/painters-delta" element={<PaintersDelta />} />
        <Route path="*" element={<HomePage />} />
      </Routes>
      <Footer links={sectionPages} pages={locationPages} />
      <CallButton />
    </>
  );
};

export default App;
